// 警報整理（SPEC #vms-web-alarm-tidy，app/vms#16；競品評估 P0）：
//   - 已恢復：狀態類警報（目前只有影像訊號中斷）同一次收到結束紀錄 → 「↺ 已恢復 HH:MM」，不自動結案
//   - 未登記 / 已移除設備的警報不算進計數（徽章、警報分頁、總覽）
//   - 已開多久：還沒結案的列寫「已開 N 天」
//   - 排序（Eddy 2026-10-18 選單，app/vms#19）：還沒處理的安防組 → 還沒處理、還沒恢復的狀態組 → 還沒處理、已恢復 →
//     處理中 → 已結案 → 未登記；前四組開最久的在前，後兩組新到舊
import type { AlarmStateName } from './alarms';
import { compareAlarm } from './alarmGroup';
import type { Episode } from './eventEpisodes';

/**
 * 狀態類警報：結束 = 狀況解除（訊號回來了），才叫「已恢復」。區域入侵、越線、拆除這類「有人做了事」的警報，
 * 結束只代表動作停了，不算已恢復。之後新增的狀態類警報加在這裡。
 */
export const RECOVERABLE_KEYS: ReadonlySet<string> = new Set(['kinio_video_signal.video_loss']);

export function isRecoverable(key: string | undefined): boolean {
  return !!key && RECOVERABLE_KEYS.has(key);
}

/** 已恢復的時間（ms）：狀態類警報、同一次有開始也有結束；不是 → null。 */
export function recoveredAt(key: string | undefined, ep: Episode | undefined): number | null {
  if (!isRecoverable(key) || !ep?.start || !ep.end) return null;
  return ep.end.ts;
}

/** 處理狀態分三種：還沒處理（open / acknowledged / 不知道）、處理中（claimed）、已結案。 */
export function handling(state: AlarmStateName | undefined): 'unhandled' | 'claimed' | 'closed' {
  if (state === 'claimed') return 'claimed';
  if (state === 'resolved' || state === 'false_alarm') return 'closed';
  return 'unhandled';
}

/**
 * 排序組別（SPEC #vms-web-alarm-tidy「排序」）：0 還沒處理的安防組（不在狀態類清單裡的：入侵、越線、拆除、門磁、偵測升級——
 * 「有人做了事」；不認得的 key 也算這組）、1 還沒處理還沒恢復的狀態組（RECOVERABLE_KEYS）、2 還沒處理已恢復、3 處理中、
 * 4 已結案、5 未登記 / 已移除設備的。
 */
export const TIDY_RANK = { security: 0, status: 1, recovered: 2, claimed: 3, closed: 4, unlisted: 5 } as const;
/** 組內開最久的在前的組（0–3）；之後的新到舊 */
const OLDEST_FIRST_MAX = TIDY_RANK.claimed;

export function tidyRank(x: { state: AlarmStateName | undefined; recovered: boolean; listed: boolean; key?: string }): number {
  if (!x.listed) return TIDY_RANK.unlisted;
  const h = handling(x.state);
  if (h === 'closed') return TIDY_RANK.closed;
  if (h === 'claimed') return TIDY_RANK.claimed;
  if (x.recovered) return TIDY_RANK.recovered;
  return isRecoverable(x.key) ? TIDY_RANK.status : TIDY_RANK.security;
}

export interface TidyKey {
  rank: number;
  /** 最早那則（合併列）的時間 ms：前四組照它由舊到新 */
  earliest: number;
  /** 這一列的時間 ms：後兩組照它新到舊 */
  ts: number;
  ref: string;
  key: string;
  id: string;
}

export function compareTidy(a: TidyKey, b: TidyKey): number {
  if (a.rank !== b.rank) return a.rank - b.rank;
  if (a.rank <= OLDEST_FIRST_MAX && a.earliest !== b.earliest) return a.earliest - b.earliest;
  return compareAlarm({ ts: a.ts, ref: a.ref, key: a.key, id: a.id }, { ts: b.ts, ref: b.ref, key: b.key, id: b.id });
}

type T = (k: string, o?: Record<string, unknown>) => string;

/** 「已開 N 分鐘 / N 小時 / N 天」（無條件捨去）；不到 1 分鐘「剛發生」。 */
export function openAgeText(t: T, sinceMs: number, now = Date.now()): string {
  const s = Math.max(0, Math.floor((now - sinceMs) / 1000));
  if (s < 60) return t('alarm.age_just_now');
  const m = Math.floor(s / 60);
  if (m < 60) return t('alarm.age_min', { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('alarm.age_hour', { count: h });
  return t('alarm.age_day', { count: Math.floor(h / 24) });
}

/** 我的設備（含通道）有沒有這個 device_ref：家戶 → 設備鍵集合。 */
export function listedSet(devices: { home_id: string; device_ref: string; children?: { device_ref: string }[] }[]): Set<string> {
  const s = new Set<string>();
  for (const d of devices) {
    s.add(`${d.home_id}/${d.device_ref}`);
    for (const c of d.children ?? []) s.add(`${d.home_id}/${c.device_ref}`);
  }
  return s;
}

/**
 * 未處理數（SPEC #vms-web-alarm-tidy「不算進計數」）：每個家戶「還沒處理」的完整清單（一次一列）扣掉未登記的。
 * homes：每個家戶的狀態——還在讀 → 整體 null（「計算中」）；完整清單讀到 → 算列；清單讀不到 → 用平台的 unhandled（不排除）；
 * 超過上限沒讀完（more）→ 平台的 unhandled 減掉讀到的未登記數，標 more。
 */
export function tidyCount(
  homes: (
    | { kind: 'pending' }
    | { kind: 'list'; listed: number; unlisted: number; more: boolean; platform?: number }
    | { kind: 'platform'; n: number }
    | { kind: 'none' }
  )[],
): { n: number | null; more: boolean } {
  if (!homes.length) return { n: null, more: false };
  let n = 0;
  let more = false;
  for (const h of homes) {
    if (h.kind === 'pending') return { n: null, more: false };
    if (h.kind === 'platform') n += h.n;
    else if (h.kind === 'list') {
      if (h.more && typeof h.platform === 'number') {
        n += Math.max(h.listed, h.platform - h.unlisted);
        more = true;
      } else n += h.listed;
    }
  }
  return { n, more };
}
